import type {
	ElasticityDataUnit,
	HTTPValidationError,
	MultipleAlertsInfoResponseModel,
	OptimizationModel,
	OptimizationStatisticModel,
	QueryScenarioRunSetRequestModel,
	QueryScenarioRunSetResponseModel,
	ScenarioModel,
	ScenarioQueryModel,
	ScenarioRunModel,
	ScenarioRunSetModel,
	ScenariosListParams,
} from '@/shared/generated'
import type { UseQueryOptions } from '@/shared/lib/query-client'
import type { AxiosError } from 'axios'

import { useQuery } from '@tanstack/react-query'

import { client } from '@/shared/api/client'
import { OptimizationStatus } from '@/shared/generated'

export const scenariosQueries = {
	scenariosRunsSet: (scenarioRunSetId: string) => ({
		queryKey: ['scenarios-run-set', scenarioRunSetId],
		queryFn: () =>
			client.scenarioRunSets.getScenarioRunSet({ scenarioRunSetId }),
		enabled: !!scenarioRunSetId,
	}),
	scenariosRunSets: (params: QueryScenarioRunSetRequestModel) => ({
		queryKey: ['scenarios-run-sets', params],
		queryFn: () => client.scenarioRunSets.listScenarioRunSets(params),
	}),
	scenarios: (params: ScenariosListParams) => ({
		queryKey: ['scenarios', params],
		queryFn: () => client.scenario.scenariosList(params),
	}),
	scenariosQueryList: (params: ScenarioQueryModel) => ({
		queryKey: ['scenarios-query-list', params],
		queryFn: () => client.scenario.scenariosQueryList(params),
	}),
	scenariosMain: (optimizationGroupId: string) => ({
		queryKey: ['main-scenario', optimizationGroupId],
		queryFn: () => client.scenario.scenarioGetMain({ optimizationGroupId }),
	}),
	scenario: (scenarioId: string) => ({
		queryKey: ['scenarios', scenarioId],
		queryFn: () => client.scenario.scenarioGet({ scenarioId }),
	}),
}

const getOptimizations = async (
	optimizationIds: string[],
	refetchInterval: number = 1000
) => {
	const delay = (ms: number): Promise<void> =>
		new Promise((resolve) => setTimeout(resolve, ms))
	while (true) {
		const optimizations = await client.optimizations.getOptimizations({
			id: optimizationIds,
		})
		if (
			optimizations?.every(({ status }) =>
				[OptimizationStatus.Finished, OptimizationStatus.Failed].includes(
					status
				)
			)
		) {
			return optimizations
		}
		await delay(refetchInterval)
	}
}

const getScenarioRunsItems = async (
	optimizationGroupId: string,
	currency?: string
): Promise<ScenarioRunsItems> => {
	const runSet = await client.scenarioRunSets.getScenarioRunSet({
		scenarioRunSetId: '',
	})
	const scenarioRuns: ScenarioRunModel[] = runSet?.scenario_runs || []
	if (!scenarioRuns.length) {
		return {} as any
	}
	const optimizationIds = scenarioRuns.map(
		({ optimization_id }) => optimization_id
	)

	const optimizations = await getOptimizations(optimizationIds)
	const [
		optimizationStatistics,
		multipleInterpretabilityStatistics,
		multipleAlertsInfo,
	] = await Promise.all([
		client.bi.getOptimizationsStatistics({
			optimizations: optimizations.map(({ id, optimization_group_id }) => ({
				optimization_id: id,
				optimization_group_id,
			})),
			currency,
		}),
		client.bi.getStatisticsMultipleInterpretability({
			optimizations: optimizations.map(({ id, optimization_group_id }) => ({
				optimization_id: id,
				optimization_group_id,
			})),
			currency,
		}),
		client.bi.getMultipleAlertsInfo({
			optimizations: optimizations.map(({ id, optimization_group_id }) => ({
				optimization_id: id,
				optimization_group_id,
			})),
			currency,
		}),
	])

	const scenarios = await client.scenario.scenariosList({
		optimization_group_id: optimizationGroupId,
	})
	const extendedScenarioRunsData: (ScenarioRunModel & {
		description?: string | null
	})[] = scenarioRuns?.map((scenario) => {
		const description = scenarios?.find(
			({ id }) => id === scenario?.scenario_id
		)?.description

		return { ...scenario, description, optimizationGroupId }
	})

	return {
		scenarioRuns: extendedScenarioRunsData,
		optimizations,
		optimizationStatistics,
		multipleInterpretabilityStatistics:
			multipleInterpretabilityStatistics as MultipleInterpretabilityStatistics[],
		multipleAlertsInfo,
	}
}
export type MultipleInterpretabilityStatistics = {
	optimization_id: string
	metrics: Record<string, number | null>
	elasticity: ElasticityDataUnit
}
type ScenarioRunsItems = {
	scenarioRuns: ScenarioRunModel[]
	optimizations: OptimizationModel[]
	optimizationStatistics: OptimizationStatisticModel[]
	multipleInterpretabilityStatistics: MultipleInterpretabilityStatistics[]
	multipleAlertsInfo: MultipleAlertsInfoResponseModel
}
export const useScenarioRunsItems = <T = ScenarioRunsItems>(
	optimizationGroupId: string,
	options?: UseQueryOptions<ScenarioRunsItems, Error, T>,
	currency?: string
) => {
	const queryParams = { optimization_group_id: optimizationGroupId, currency }

	return useQuery<ScenarioRunsItems, Error, T>({
		queryKey: ['scenarios-runs-items', queryParams],
		queryFn: () => getScenarioRunsItems(optimizationGroupId, currency),
		...(options || {}),
	})
}

/**
 * Hook for get scenarios in optimization group
 * @param optimizationGroupId - Optimization group id
 * @param options - Query options
 * @returns - Query result
 */
export const useScenariosQuery = <T = ScenarioModel[]>(
	optimizationGroupId?: string,
	options?: UseQueryOptions<ScenarioModel[], AxiosError<HTTPValidationError>, T>
) => {
	const queryParams = optimizationGroupId
		? { optimization_group_id: optimizationGroupId }
		: {}

	return useQuery<ScenarioModel[], AxiosError<HTTPValidationError>, T>({
		...scenariosQueries.scenarios(queryParams),
		...(options || {}),
	})
}

/**
 * Hook for get scenario by id
 * @param scenarioId - Scenario id
 * @param options - Query options
 * @returns - Query result
 */
export const useScenarioQuery = <T = ScenarioModel>(
	scenarioId: string,
	options?: UseQueryOptions<ScenarioModel, Error, T>
) =>
	useQuery<ScenarioModel, Error, T>({
		...scenariosQueries.scenario(scenarioId),
		...(options || {}),
	})

export const useScenariosRunsSetsQuery = <T = QueryScenarioRunSetResponseModel>(
	params: QueryScenarioRunSetRequestModel,
	options?: UseQueryOptions<
		QueryScenarioRunSetResponseModel,
		HTTPValidationError,
		T
	>
) =>
	useQuery<QueryScenarioRunSetResponseModel, HTTPValidationError, T>({
		...scenariosQueries.scenariosRunSets(params),
		...(options || {}),
	})

export const useScenariosRunsSetQuery = <T = ScenarioRunSetModel>(
	scenarioRunSetId: string,
	options?: UseQueryOptions<ScenarioRunSetModel, HTTPValidationError, T>
) =>
	useQuery<ScenarioRunSetModel, HTTPValidationError, T>({
		...scenariosQueries.scenariosRunsSet(scenarioRunSetId),
		...(options || {}),
	})

export const useMainScenarioQuery = <T = ScenarioModel>(
	optimizationGroupId: string,
	options?: UseQueryOptions<ScenarioModel, AxiosError<HTTPValidationError>, T>
) =>
	useQuery<ScenarioModel, AxiosError<HTTPValidationError>, T>({
		...scenariosQueries.scenariosMain(optimizationGroupId),
		...(options || {}),
	})

export const useScenarioQueryListQuery = <T = ScenarioModel[]>(
	queryParams: ScenarioQueryModel,
	options?: UseQueryOptions<ScenarioModel[], AxiosError<HTTPValidationError>, T>
) =>
	useQuery<ScenarioModel[], AxiosError<HTTPValidationError>, T>({
		...scenariosQueries.scenariosQueryList(queryParams),
		...(options || {}),
	})
